import {
  AfterContentInit,
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  DestroyRef,
  EmbeddedViewRef,
  inject,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Color } from '../../../models/common/color.model';
import { Ids } from '../../../models/common/ids.model';
import { PhotographerBrand, PhotographerCategory } from '../../../models/common/photographer.models';
import { ShotType } from '../../../models/common/shot-type.model';
import { TemplateShotTypeDTI, UpdateTemplate } from '../../../models/company/update-template.models';
import { ImgPreviewComponent } from '../../../shared/components/img-preview/img-preview.component';
import { BasicFile } from '../../../shared/models/basic-file.model';
import { UtilitiesService } from '../../../shared/services/utilities.service';

import { opacityAnimation } from '../../../animations/opacity.animation';
import { Brand } from '../../../models/common/brand.model';
import { Category } from '../../../models/common/category.model';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import { SizeGroup } from '../../../models/common/size-group.model';
import { Style } from '../../../models/common/style.model';
import { Template, TemplateShotType } from '../../../models/company/template.models';
import { CompanyService } from '../../../services/company.service';
import { AutocompleteSettings } from '../../../shared/models/autocomplete-settings.model';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { MediaFormat } from '../../../models/common/media-format.type';
import { AspectRatio } from '../../../models/common/aspect-ratio.type';
import { CodifiedUsed } from '../../../models/common/codified-used.type';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GraphQLService } from '../../../services/graph-ql.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { map } from 'rxjs';
import { WrapperResultGraphQL } from '../../../models/shared/types/wrapper-result-graphql.type';
import { PageInfo } from '../../../models/shared/types/page-info.type';
import { AppService } from '../../../app.service';
import { MutationQuery } from '../../../models/shared/enums/mutation-query.enum';
import { CompanyRole } from '../../../models/shared/enums/company-role.enum';
import { FilterService } from '../../../shared/services/filter.service';
import { BrandFilterService } from '../../../shared/services/brand-filter.service';
import { CategoryFilterService } from '../../../shared/services/category-filter.service';
import { IFilterListService } from '../../../shared/services/ifilter-list.service';
import { FilterListComponent } from '../../../shared/components/filter-list/filter-list.component';
import { MatDialog } from '@angular/material/dialog';
import { Selectable } from '../../../shared/models/types/selectable.type';

type SingleFilterDialogData<T> = {
  title: string,
  selected?: Selectable<T>,
  filterService: IFilterListService<T>,
  variables?: any,
  singleSelection?: boolean
}

@Component({
  standalone: false,
  selector: 'bt-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
  animations: [opacityAnimation]
})
export class TemplatesComponent implements OnInit, AfterContentInit {

  @ViewChild('styleItemTemplate', { static: true }) public styleItemTemplate: TemplateRef<any>;
  readonly #destroyRef = inject(DestroyRef);
  readonly #graphQLService = inject(GraphQLService);
  readonly #appService = inject(AppService);
  readonly #filterService = inject(FilterService);
  readonly #dialog = inject(MatDialog);

  readonly editRoles = [CompanyRole.TEMPLATES_EDIT]

  filtersList = [
    { id: 'brandsFilter', label: 'Brands' },
    { id: 'categoriesFilter', label: 'Categories' },
  ];
  brandsFilter: SingleFilterDialogData<Brand> = {
    title: 'Brands',
    selected: undefined,
    filterService: inject(BrandFilterService),
    singleSelection: true
  };
  categoriesFilter: SingleFilterDialogData<Category> = {
    title: 'Categories',
    selected: undefined,
    filterService: inject(CategoryFilterService),
    variables: { brandIds: [] },
    singleSelection: true
  };

  templates: { data: Array<Template>, dataToView?: Array<Template>, search: string, tmp?: Template, paginationPage?: number, paginationSize: number } = {
    data: [],
    search: '',
    paginationSize: 15
  };
  brands: { data: Array<Brand>, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
    data: [],
    autocompleteSettings: new AutocompleteSettings({ textProperty: 'brandName', valueProperty: 'brandId' }),
    loaderId: 'brandsLoaderId'
  };
  categories: { data: Array<Category>, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
    data: [],
    autocompleteSettings: new AutocompleteSettings({
      textProperty: 'name',
      valueProperty: 'id',
      placeholder: '- All categories -'
    }),
    loaderId: 'categoriesLoaderId'
  };
  styles: { data: Array<Style>, nextData?: Array<Style>, multiselectSettings: AutocompleteSettings, page?: number, search: string, loaderId: string } = {
    data: [],
    multiselectSettings: new AutocompleteSettings({
      textProperty: 'fullName',
      valueProperty: 'modelId',
      placeholder: '- All styles -',
      paginationSize: 50,
      serverPagination: true,
      showSearch: true
    }),
    loaderId: 'stylesLoaderId',
    search: ''
  };
  colors: { data: Array<Color>, nextData?: Array<Color>, multiselectSettings: AutocompleteSettings, page?: number, search: string, loaderId: string, pageInfo: PageInfo } = {
    data: [],
    multiselectSettings: new AutocompleteSettings({
      textProperty: 'fullName',
      valueProperty: 'colorId',
      placeholder: '- All colors -',
      paginationSize: 50,
      serverPagination: true,
      showSearch: true
    }),
    loaderId: 'colorsLoaderId',
    search: '',
    pageInfo: {
      startCursor: null,
      endCursor: null,
      hasNextPage: false,
      hasPreviusPage: false
    }
  };
  shotTypes: { data: Array<ShotType>, page?: number, size: number, search: string, loaderId: string, tmpIndex?: number, inputTimer?: any, pageInfo: PageInfo } = {
    data: [],
    size: 50,
    loaderId: 'colorsLoaderId',
    search: '',
    pageInfo: {
      startCursor: null,
      endCursor: null,
      hasNextPage: false,
      hasPreviusPage: false
    }
  };
  sizeGroups: { data?: Array<SizeGroup>, selected?: SizeGroup, disabledDeleteAction?: boolean, loaderId: string } = {
    loaderId: 'sizegroupsLoaderId'
  };
  mediaTypes: { data: Array<MediaFormat>, page?: number, size: number, search: string, loaderId: string, tmpIndex?: number, inputTimer?: any } = {
    data: [],
    size: 50,
    loaderId: 'mediaTypesLoaderId',
    search: ''
  };
  aspectRatios: { data: Array<AspectRatio>, page?: number, size: number, search: string, loaderId: string, tmpIndex?: number, inputTimer?: any } = {
    data: [],
    size: 50,
    loaderId: 'aspectRatiosLoaderId',
    search: ''
  };
  codifiedUseds: { data: Array<CodifiedUsed>, page: number, size: number, search: string, loaderId: string, tmpIndex?: number, inputTimer?: any, pageInfo: PageInfo } = {
    data: [],
    page: 0,
    size: 50,
    loaderId: 'codifiedUsedsLoaderId',
    search: '',
    pageInfo: {
      startCursor: null,
      endCursor: null,
      hasNextPage: false,
      hasPreviusPage: false
    }
  };

  sidebarTypeEnum = SidebarTypeEnum;
  currentSidebar: SidebarTypeEnum;
  changeAllColumn: boolean;
  loaderId = 'templatesLoaderId';
  sidebarLoaderId = 'sidebarLoaderId';
  updateLoaderId = 'updateLoaderId';
  differentShotsForSizeGroups: boolean;

  showStylesOptions: boolean;
  tryToSave: boolean;
  maxShotTypes = 5;
  optionsHover: boolean;

  // brandsFilters: { data: Array<Brand>, selected?: Brand, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
  //   data: [],
  //   autocompleteSettings: new AutocompleteSettings({ textProperty: 'brandName', valueProperty: 'brandId' }),
  //   loaderId: UtilityFunctions.newGuid()
  // };

  // categoriesFilter: { data: Array<Category>, selected?: Category, autocompleteSettings: AutocompleteSettings, loaderId: string } = {
  //   data: [],
  //   autocompleteSettings: new AutocompleteSettings({
  //     textProperty: 'name',
  //     valueProperty: 'id',
  //     placeholder: '- All categories -'
  //   }),
  //   loaderId: UtilityFunctions.newGuid()
  // };

  brandsFiltersSelected: { cached: Array<PhotographerBrand>, current?: Array<PhotographerBrand> } = { cached: [] };

  constructor(
    private companyService: CompanyService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector,
    private utilitiesService: UtilitiesService
  ) { }

  ngOnInit() {
    const params = this.#filterService.getDataFromUrl();
    this.getTemplates(0, params);

    this.getAspectRatios(0);
    this.getMediaTypes(0);
    this.getCodifiedUsed(0);
  }

  ngAfterContentInit(): void {
    //Acquisizione dei parametri dell'url
    this.getData();
  }

  openDialogFilter(title: string = ''): void {
    const initialValue = !!this[title].selected ? [this[title].selected] : [];
    const dialog = this.#dialog.open(FilterListComponent<any>, {
      data: { title: this[title].title, selected: !!this[title].selected ? [this[title].selected] : [], filterService: this[title].filterService, variables: this[title].variables, singleSelection: this[title].singleSelection },
      height: '70%',
      width: 'auto',
      minWidth: '25%'
    });

    dialog.afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.applyFilters(title, res[0])
            if (title === 'brandsFilter')
              this.categoriesFilter.variables.brandIds = res.map(el => el.brandId)
          } else {
            this[title].selected = initialValue[0];
          }
        }
      })
  }

  getLabel(filter: string): string {
    return this[filter].selected ? `: ${this[filter].selected.displayName}` : '';
  }

  getFullTitleFilter(filter: string): string {
    return this[filter].selected ? this[filter].selected.displayName : '';
  }

  clearFilter(event: any, filter: string): void {
    event.stopPropagation();
    this[filter].selected = undefined;
    (this[filter] as SingleFilterDialogData<any>).filterService.clearSelection();
    this.#filterService.removeDataFromUrl(filter, () => null);
    if (filter === 'brandsFilters') this['categoriesFilter'].variables.brandIds = [];

    this.getTemplates(0);
  }

  applyFilters(filter: string, selected: Selectable<any>) {
    if (!selected) {
      // Rimozione il parametro dall'url
      this.#filterService.removeDataFromUrl(filter, () => null);
    } else {
      this[filter].selected = selected;
      const ids = selected.displayId;
      this.#filterService.setQueryParams({ [filter]: ids });
    }

    this.getTemplates(0);
  }

  getData() {
    const params = this.#filterService.getDataFromUrl();
    this.getParams(params);
  }

  //Prende e assegna i parametri dell'url
  private getParams(params: { [key: string]: string }): void {
    if (params['brandsFilter']) {
      //const brandParams = this.parseBrands(params['brands']);
      const ids = params['brandsFilter'].split('-').map(el => +el);
      this.getBrandsSelected(ids);
    } else {
      this.brandsFilter.selected = undefined;
    }

    if (params['categoriesFilter']) {
      const ids = params['categoriesFilter'].split('-').map(el => +el);
      this.getCategoriesSelected(ids);
    } else {
      this.categoriesFilter.selected = undefined;
    }

    if (params['search'])
      this.templates.search = params['search'];

  }

  private getBrandsSelected(brandIds: Array<number>) {
    const filters = { first: 50, after: null, search: '', brandIds, companyId: this.#appService.companyId };
    this.#graphQLService.getData<Brand>(GetQuery.GET_BRANDS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['brands']))
      .subscribe((data: WrapperResultGraphQL<any>) => {
        this['brandsFilter'].selected = data.nodes.map(el => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup, displayId: el.brandId!, displayName: el.brand.name, disabled: false, selected: false }))[0];
        this.categoriesFilter.variables.brandIds = this['brandsFilter'].selected.brandId;
      });
  }

  private getCategoriesSelected(categoryIds: Array<number>) {
    const filters = { first: 50, after: null, search: '', categoryIds };
    this.#graphQLService.getData<Category>(GetQuery.GET_CATEGORIES_BY_IDS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['category']))
      .subscribe((data: WrapperResultGraphQL<Category>) => {
        this['categoriesFilter'].selected = data.nodes.map(el => ({ ...el, displayId: el.id!, displayName: el.name, disabled: false, selected: true }))[0];
      });
  }

  inViewportAction(ev: any, item: Template) {
    item.isVisible = ev.isVisible;
    if (item.isVisible) {
      const index = this.templates.data.findIndex(t => t.parentTemplateId === item.parentTemplateId);
      if (index === this.templates.data.length - 1 && this.templates.data.length / this.templates.paginationSize === this.templates.paginationPage + 1) {
        this.getTemplates(this.templates.paginationPage + 1);
      }
    }
  }

  onDeleteTemplate(t: Template) {
    this.templates.tmp = t;
    this.currentSidebar = SidebarTypeEnum.deleteAction;
  }

  deleteTemplate() {
    const variables = {
      companyId: this.#appService.companyId,
      parentTemplateId: this.templates.tmp!.parentTemplateId,
      currentUserId: this.#appService.user.userId
    };
    this.#graphQLService.postData(MutationQuery.DELETE_TEMPLATE, variables)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.onExitSidebar();
          this.getTemplates(0);
        },
        error: () => {
          this.onExitSidebar();
          this.utilitiesService.showSnackbar('Missing fields to save template');
        }
      });
    // this.companyService.deleteTemplate(this.templates.tmp.parentTemplateId, this.sidebarLoaderId).subscribe(() => {
    //   this.onExitSidebar();
    //   this.getTemplates(0);
    // });
  }

  onEditTemplate(t: Template) {
    t.inEditing = true;
    this.templates.tmp = UtilityFunctions.DeepClone(t);
    this.differentShotsForSizeGroups = t.shotTypes.some(x => !!x.sizeGroupId);
    if (t.hasSizeGroup) { this.getSizeGroups(t.brandId); }
  }

  cancelEditTemplate(t?: Template) {
    this.brands.data = null;
    this.resetCategoriesData();
    this.tryToSave = false;
    this.templates.tmp = null;
    if (t) {
      t.inEditing = false;
      if (!t.parentTemplateId) {
        this.templates.data.splice(0, 1);
        this.setTemplatesToView();
      }
    }
  }

  onSaveTemplate() {
    if (!this.templates.tmp.shotTypes.some(x => !x.shotTypeCode)) {
      this.tryToSave = false;
      const updateTemplate = new UpdateTemplate({
        parentTemplateId: this.templates.tmp.parentTemplateId || null,
        brandId: this.templates.tmp.brandId,
        categoryId: this.templates.tmp.categoryId || null,
        modelIds: this.templates.tmp.styles.map(s => new Ids(s.modelId)),
        colorIds: this.templates.tmp.colors.map(s => new Ids(s.colorId)),
        shotTypes: this.templates.tmp.shotTypes.map((type, index) => new TemplateShotTypeDTI({
          shotTypeId: type.shotTypeId || null,
          shotTypeName: type.shotTypeName || '',
          shotTypeCode: type.shotTypeCode,
          sizeGroupId: type.sizeGroupId,
          aspectRatioId: type.aspectRatioId,
          mediaFormatId: type.mediaFormatId,
          codifiedUsedId: type.codifiedUsedId,
          ord: index + 1
        }))
      });
      this.#graphQLService.postData(MutationQuery.UPDATE_TEMPLATE, { updateTemplate, companyId: this.#appService.companyId, currentUserId: this.#appService.user.userId })
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.cancelEditTemplate();
          this.getTemplates(0);
        });
    } else {
      this.tryToSave = true;
      this.utilitiesService.showSnackbar('Missing fields to save template');
    }
  }

  onAddShotType(index?: number) {
    if (!this.templates.tmp.hasSizeGroup || !this.templates.tmp.shotTypes[index + this.maxShotTypes - 1] || this.templates.tmp.shotTypes[index].sizeGroupId !== this.templates.tmp.shotTypes[index + this.maxShotTypes - 1].sizeGroupId) {
      const shotType = new TemplateShotType({
        shotTypeId: null,
        shotTypeName: '',
        sizeGroupId: index >= 0 ? this.templates.tmp.shotTypes[index].sizeGroupId : null,
        sizeGroupName: index >= 0 ? this.templates.tmp.shotTypes[index].sizeGroupName : null,
        aspectRatioId: 0,
        mediaFormatId: 0,
        codifiedUsedId: 0,
        codifiedUsedCode: undefined,
        codifiedUsedName: undefined
      });
      let newIndex = this.templates.tmp.shotTypes.length;
      if (this.templates.tmp.hasSizeGroup) {
        this.templates.tmp.shotTypes.some((t, i) => {
          if (i > index && t.sizeGroupId !== this.templates.tmp.shotTypes[i - 1].sizeGroupId) { newIndex = i; }
          return newIndex < this.templates.tmp.shotTypes.length;
        });
        shotType.sizeIndex = newIndex - index + 1;
      }
      this.templates.tmp.shotTypes.splice(newIndex, 0, shotType);
      this.setSizeGroupsBooleans();
    }
  }

  onRemoveSizeGroup(type: TemplateShotType) {
    this.templates.tmp.shotTypes = this.templates.tmp.shotTypes.filter(x => x.sizeGroupId !== type.sizeGroupId);
    this.setSizeGroupsBooleans();
  }

  onRemoveShotType(index: number) {
    this.templates.tmp.shotTypes.splice(index, 1);
    if (this.templates.tmp.hasSizeGroup && this.differentShotsForSizeGroups) {
      this.setSizeGroupsBooleans();
      this.templates.tmp.shotTypes.forEach((s, i) => s.sizeIndex = !i || this.templates.tmp.shotTypes[i - 1].sizeGroupId !== s.sizeGroupId ? 0 : this.templates.tmp.shotTypes[i - 1].sizeIndex + 1);
    }
  }

  // onFocus Handler
  onFocusShotType(index: number) {
    this.shotTypes.tmpIndex = index;
    setTimeout(() => {
      this.templates.tmp!.shotTypes[index].showOptions = true;
      if (!this.shotTypes.data.length) this.getShotTypes();
    });
  }

  // onBlur Handler
  onBlurShotType(index: number) {
    if (!this.optionsHover) this.templates.tmp.shotTypes[index].showOptions = false;
  }

  // onSelect Handler
  onSelectShotType(t: ShotType) {
    const type = new TemplateShotType({
      shotTypeCode: t.code,
      shotTypeId: t.id,
      shotTypeName: t.name,
      aspectRatioId: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].aspectRatioId,
      mediaFormatId: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].mediaFormatId,
      codifiedUsedId: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].codifiedUsedId,
      codifiedUsedCode: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].codifiedUsedCode,
      codifiedUsedName: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].codifiedUsedName,
      showOptions: false,
      sizeGroupId: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].sizeGroupId,
      sizeGroupName: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].sizeGroupName,
      sizeIndex: this.templates.tmp.shotTypes[this.shotTypes.tmpIndex].sizeIndex
    });
    this.templates.tmp.shotTypes.splice(this.shotTypes.tmpIndex, 1, type);
    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.forEach(tp => {
        if (tp.sizeIndex === type.sizeIndex && tp.sizeGroupId !== type.sizeGroupId) {
          tp.shotTypeId = type.shotTypeId;
          tp.shotTypeCode = type.shotTypeCode;
          tp.shotTypeName = type.shotTypeName;
        }
      });
    }
  }

  onAddNewTemplate() {
    this.templates.data.forEach(el => el.openedDetails = false);
    const newTemmplate = new Template({
      inEditing: true,
      openedDetails: true,
      styles: [],
      shotTypes: [],
      colors: [],
      shotsNumber: undefined,
      parentTemplateId: undefined,
      templateName: 'New template'
    });
    this.templates.tmp = UtilityFunctions.DeepClone(newTemmplate);
    this.templates.data.unshift(newTemmplate);
    this.setTemplatesToView();
    this.getBrands();
  }

  onSelectBrand(brand: Brand) {
    this.sizeGroups.data = null;
    this.sizeGroups.selected = null;
    this.changeAllColumn = false;
    this.differentShotsForSizeGroups = false;
    this.templates.tmp.brandId = brand ? brand.brandId : null;
    this.templates.tmp.brandName = brand ? brand.brandName : null;
    this.templates.tmp.hasSizeGroup = brand ? brand.hasSizeGroup : null;
    if (brand) {
      this.templates.tmp.shotTypes = [new TemplateShotType({ sizeGroupId: null, sizeGroupName: null, shotTypeId: null, shotTypeName: null })];
      this.getCategories(this.templates.tmp!.brandId);
    } else {
      this.templates.tmp.shotTypes = [];
      //this.resetCategoriesData();
    }
  }

  onSelectCategory(category: Category) {
    this.templates.tmp.categoryId = category ? category.id : null;
    this.templates.tmp.categoryName = category ? category.name : null;
    !!category ? this.getCategoryStyles(this.templates.tmp.brandId, this.templates.tmp.categoryId, 0) : this.resetStylesData();
  }

  onSelectStyles(styles: Array<Style>) {
    this.templates.tmp.styles = styles;
    if (this.colors.data) {
      this.colors.data = null;
      this.colors.nextData = null;
    }
  }

  onSelectColors(colors: Array<Color>) {
    this.templates.tmp.colors = colors;
  }

  onExitSidebar() {
    this.templates.tmp = null;
    this.currentSidebar = null;
  }

  onMultiselectFilterChange(text: string) {
    this.styles.search = text;
    this.getCategoryStyles(this.templates.tmp.brandId, this.templates.tmp.categoryId, 0);
  }

  onColorsMultiselectFilterChange(text: string) {
    this.colors.search = text;
    this.getColorsByModel();
  }

  onMultiselectNextpage() {
    this.getCategoryStyles(this.templates.tmp.brandId, this.templates.tmp.categoryId, this.styles.page + 1);
  }

  onColorsMultiselectNextpage() {
    if (this.templates.tmp?.styles && this.templates.tmp.styles[0])
      this.getColorsByModel();
  }

  addSizeGroup() {
    this.templates.tmp!.shotTypes.push(new TemplateShotType({
      shotTypeId: undefined,
      shotTypeName: undefined,
      sizeGroupId: this.sizeGroups.selected!.id,
      sizeGroupName: this.sizeGroups.selected!.name,
      sizeIndex: 0
    }));
    this.sizeGroups.selected = undefined;
    this.setSizeGroupsBooleans();
  }

  // onChange Handler
  onChangeShotTypeName(type: TemplateShotType) {
    type.shotTypeId = null;
    if (this.templates.tmp.hasSizeGroup && this.changeAllColumn) {
      this.templates.tmp.shotTypes.filter(t => t.sizeIndex === type.sizeIndex && t.sizeGroupId !== type.sizeGroupId).forEach(t => t.shotTypeName = type.shotTypeName);
    }
  }

  onChangeShotTypeCode(type: TemplateShotType) {
    type.shotTypeId = null;
    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.filter(t => t.sizeIndex === type.sizeIndex && t.sizeGroupId !== type.sizeGroupId).forEach(t => t.shotTypeCode = type.shotTypeCode);
    }
  }

  onClickTemplate(template: Template) {
    setTimeout(() => {
      template.openedDetails = !template.openedDetails;
      this.templates.data.filter(x => x.parentTemplateId !== template.parentTemplateId).forEach(t => t.openedDetails = false);
      const op = this.templates.data.find(t => t.openedDetails && t.parentTemplateId !== template.parentTemplateId);
      if (op) op.openedDetails = false;
    });
  }

  onOpenColorsMultiselect() {
    if (!this.colors.data || !this.colors.data.length) this.getColorsByModel();
  }

  // onFilterChange Handler
  onShotTypesFilterChange() {
    clearTimeout(this.shotTypes.inputTimer);
    this.shotTypes.inputTimer = setTimeout(() => {
      this.getShotTypes();
    }, 750);
  }

  shotTypesInViewportAction(ev: any, t: ShotType) {
    if (ev.isVisible) {
      const index = this.shotTypes.data.findIndex(el => el.id === t.id);
      if (index === this.shotTypes.data.length - 1 && this.shotTypes.data.length % this.shotTypes.size === 0) {
        this.getShotTypes();
      }
    }
  }

  onStyleSketch(ev: any, style: Style) {
    ev.stopPropagation();
    if (style.sketchImgPath) {
      const componentRef = this.componentFactoryResolver.resolveComponentFactory(ImgPreviewComponent).create(this.injector);
      componentRef.instance.file = new BasicFile({
        FileName: style.fullName,
        FilePath: style.sketchImgPath
      });
      componentRef.instance.closed.subscribe(() => {
        this.applicationRef.detachView(componentRef.hostView);
        componentRef.destroy();
      });
      this.applicationRef.attachView(componentRef.hostView);
      const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
      this.showStylesOptions = false;
    }
  }

  // onOpenFilters() {
  //   this.currentSidebar = SidebarTypeEnum.filters;
  //   this.brandsFiltersSelected.current = UtilityFunctions.DeepClone(this.brandsFiltersSelected.cached);
  // }

  // onExitFilters() {
  //   this.onExitSidebar();
  // this.brandsFilters.data = null;
  // this.brandsFilters.selected = null;
  // this.categoriesFilter.data = null;
  // this.categoriesFilter.selected = null;
  // this.brandsFiltersSelected.current = null;
  // }

  onOpenBrandsAutocomplete() {
    // if (!this.brandsFilters.data) this.getBrands(true);
  }

  onSelectBrandFilters(b: Brand) {
    this.brandsFilter.selected = { ...b, displayId: b.brandId!, displayName: b.brandName!, selected: true, disabled: false };
    if (!this.brandsFilter.selected)
      this.categoriesFilter.variables.brandIds = this.brandsFilter.selected!.brandId!
    // !this.brandsFilters.selected ? this.categoriesFilter.selected = null : this.getCategories(this.brandsFilters.selected.brandId, true);
  }

  onSelectCategoryFilters(c: Category) {
    this.categoriesFilter.selected = { ...c, displayId: c.id, displayName: c.name, selected: true, disabled: false };
  }

  addBrandFilter() {
    let brand = this.brandsFiltersSelected.current.find(x => x.brandId == this.brandsFilter.selected[0].brandId);
    if (!brand) {
      brand = new PhotographerBrand({
        brandId: this.brandsFilter.selected.brandId,
        brandName: this.brandsFilter.selected.brandName,
        categories: this.categoriesFilter.selected ? [new PhotographerCategory({
          brandId: this.brandsFilter.selected.brandId,
          categoryId: this.categoriesFilter.selected.id,
          categoryName: this.categoriesFilter.selected.name
        })] : []
      });
      this.brandsFiltersSelected.current.push(brand);
      this.brandsFilter.selected = null;
      this.categoriesFilter.selected = null;
    } else if (this.categoriesFilter.selected && !!brand.categories.length && !brand.categories.some(x => x.categoryId === this.categoriesFilter.selected[0]?.id)) {
      brand.categories.push(new PhotographerCategory({
        brandId: brand.brandId,
        categoryId: this.categoriesFilter.selected[0].id,
        categoryName: this.categoriesFilter.selected[0].name
      }));
      this.brandsFilter.selected = null;
      this.categoriesFilter.selected = null;
    } else {
      this.utilitiesService.showSnackbar('Addition conflicts with already present data');
    }
  }

  removeSidebarBrandFilter(b: PhotographerBrand, c?: PhotographerCategory) {
    this.brandsFilter.selected = undefined;
    this.brandsFiltersSelected.current = this.brandsFiltersSelected.current.filter(x => this.manageBrandFilter(x, b, c));
  }

  removeBrandFilter(b: PhotographerBrand, c?: PhotographerCategory) {
    this.brandsFiltersSelected.cached = this.brandsFiltersSelected.cached.filter(x => this.manageBrandFilter(x, b, c));
    this.getTemplates(0);
  }

  // applyFiltersOld() {
  //   this.brandsFiltersSelected.cached = this.brandsFiltersSelected.current;
  //   this.onExitFilters();
  //   this.getTemplates(0);
  // }

  onDifferentShotsForSizeGroups() {
    if (this.differentShotsForSizeGroups) {
      this.templates.tmp!.shotTypes = [];
      this.getSizeGroups(this.templates.tmp!.brandId);
    } else {
      this.sizeGroups.data = undefined;
      this.sizeGroups.selected = undefined;
      this.templates.tmp!.shotTypes = [new TemplateShotType({ sizeGroupId: undefined, sizeGroupName: undefined, shotTypeId: undefined, shotTypeName: undefined })];
    }
  }

  private manageBrandFilter(brandToCheck: PhotographerBrand, brandFilter: PhotographerBrand, category?: PhotographerCategory): boolean {
    let flag = true;
    if (!category) {
      flag = brandToCheck.brandId !== brandFilter.brandId;
    } else if (brandToCheck.brandId === brandFilter.brandId) {
      brandFilter.categories = brandFilter.categories.filter(cat => category.categoryId !== cat.categoryId);
      flag = !!brandFilter.categories.length;
    }
    return flag;
  }

  private setSizeGroupsBooleans() {
    if (this.templates.tmp.hasSizeGroup && this.differentShotsForSizeGroups) {
      this.sizeGroups.data.forEach(s => s.disabled = this.templates.tmp.shotTypes.some(t => t.sizeGroupId === s.id));
      this.sizeGroups.disabledDeleteAction = this.sizeGroups.data.filter(s => s.disabled).length === 1;
    }
  }

  private resetCategoriesData() {
    this.categories.data = null;
    this.templates.tmp.categoryId = null;
    this.templates.tmp.categoryName = null;
    this.resetStylesData();
  }

  private resetStylesData() {
    this.templates.tmp.styles = [];
    this.styles.data = null;
    this.styles.nextData = null;
    this.styles.search = null;
    this.resetColorsData();
  }

  private resetColorsData() {
    this.templates.tmp.colors = [];
    this.colors.data = null;
    this.colors.nextData = null;
    this.colors.search = null;
  }

  private setTemplatesToView() {
    const search = this.templates.search.toLowerCase();
    this.templates.dataToView = this.templates.data.filter(t => t.parentTemplateId === null || t.templateName.toLowerCase().indexOf(search) >= 0);
  }

  // Getter
  private getTemplates(page: number, params?: any) {
    this.templates.paginationPage = page;
    const brandIds = params && params['brandsFilter'] ? params['brandsFilter'].split('-').map((el: string) => +el) : [];
    const categoryIds = params && params['categoriesFilter'] ? params['categoriesFilter'].split('-').map((el: string) => +el) : [];

    const filters = {
      companyId: this.#appService.companyId,
      paginationPage: this.templates.paginationPage,
      paginationSize: this.templates.paginationSize,
      brandIds: params ? brandIds : this.brandsFilter.selected ? this.brandsFilter.selected.displayId : [],
      categoryIds: params ? categoryIds : this.categoriesFilter.selected ? this.categoriesFilter.selected.displayId : []
    }

    this.#graphQLService.getData<Template>(GetQuery.GET_TEMPLATES, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['templates']))
      .subscribe((data: WrapperResultGraphQL<Template>) => {
        this.templates.data = UtilityFunctions.DeepClone(data.nodes);
        if (this.templates.tmp) {
          const ex = !this.templates.tmp.parentTemplateId && this.templates.data[0] ? this.templates.data[0] : this.templates.data.find(t => t.parentTemplateId === this.templates.tmp?.parentTemplateId);
          if (ex) ex.openedDetails = true;
          this.templates.tmp = undefined;
        }
        this.setTemplatesToView();
      });
  }

  private getBrands(fromFilters?: boolean) {
    this.#graphQLService.getData<Brand>(GetQuery.GET_BRANDS_NO_IDS, { search: '', companyId: Number(this.#appService.companyId) })
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((res: any) => res['data']['brands'])
      )
      .subscribe({
        next: (res: WrapperResultGraphQL<any>) => {
          fromFilters ? this.brands.data = res.nodes.map(el => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup })) : this.brands.data = res.nodes.map(el => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup }));
        }
      })
  }

  private getCategories(brandId: number, fromFilters?: boolean) {
    this.#graphQLService.getData<Category>(GetQuery.GET_CATEGORIES_NO_IDS, { brandIds: [brandId] })
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((res: any) => res['data']['category'])
      )
      .subscribe({
        next: (data: WrapperResultGraphQL<Category>) => {
          fromFilters ? this.categories.data = UtilityFunctions.DeepClone(data.nodes) : this.categories.data = UtilityFunctions.DeepClone(data.nodes);
        }
      })
  }

  private getCategoryStyles(brandId: number, categoryId: number, page: number) {
    this.styles.page = page;
    const filter = {
      brandId,
      categoryId,
      paginationPage: page,
      paginationSize: this.styles.multiselectSettings.paginationSize,
      search: this.styles.search
    }

    this.#graphQLService.getData<Style>(GetQuery.GET_CATEGORY_STYLE, filter)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((res: any) => res['data']['categoryStyle'])
      )
      .subscribe({
        next: (data: WrapperResultGraphQL<Style>) => {
          !this.styles.page ? this.styles.data = UtilityFunctions.DeepClone(data.nodes) : this.styles.nextData = UtilityFunctions.DeepClone(data.nodes);
        }
      })
  }

  private getColorsByModel() {
    const modelId = this.templates.tmp!.styles[0].modelId;
    const filter = {
      first: this.colors.multiselectSettings.paginationSize,
      after: this.colors.pageInfo.endCursor,
      modelId,
      search: this.colors.search
    };
    this.#graphQLService.getData<Color>(GetQuery.GET_COLORS_BY_MODEL, filter)
      .pipe(map((res: any) => res['data']['colors']), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: WrapperResultGraphQL<any>) => {
          !this.colors.pageInfo.endCursor ? this.colors.data = data.nodes.map(el => ({ colorId: el.id, colorCode: el.code, colorName: el.finalColorName, fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}` })) : this.colors.nextData = data.nodes.map(el => ({ colorId: el.id, colorCode: el.code, colorName: el.finalColorName, fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}` }));
          this.colors.pageInfo = { ...data.pageInfo };
        }
      });
  }

  private getSizeGroups(brandId: number) {
    this.#graphQLService.getData(GetQuery.GET_SIZE_GROUPS_BY_BRAND, { brandId })
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['sizeGroupsByBrand']))
      .subscribe((data: WrapperResultGraphQL<any>) => {

        this.sizeGroups.disabledDeleteAction = false;
        this.sizeGroups.data = UtilityFunctions.DeepClone(data.nodes);
        console.log(this.sizeGroups.data)
        !this.templates.tmp?.parentTemplateId ?
          this.sizeGroups.data.forEach(s => {
            s.disabled = true;
            this.templates.tmp!.shotTypes.push(new TemplateShotType({
              shotTypeId: undefined,
              shotTypeName: undefined,
              sizeGroupId: s.id,
              sizeGroupName: s.name,
              sizeIndex: 0
            }));
          })
          :
          this.setSizeGroupsBooleans();
      });

    // this.companyService.getSizeGroups(brandId, this.sizeGroups.loaderId).subscribe(data => {
    //   this.sizeGroups.disabledDeleteAction = false;
    //   this.sizeGroups.data = data;
    //   !this.templates.tmp?.parentTemplateId ?
    //     this.sizeGroups.data.forEach(s => {
    //       s.disabled = true;
    //       this.templates.tmp!.shotTypes.push(new TemplateShotType({
    //         shotTypeId: undefined,
    //         shotTypeName: undefined,
    //         sizeGroupId: s.id,
    //         sizeGroupName: s.name,
    //         sizeIndex: 0
    //       }));
    //     }) 
    //     :
    //     this.setSizeGroupsBooleans();
    // });
  }

  private getShotTypes() {
    const filter = {
      first: this.shotTypes.size,
      after: this.shotTypes.pageInfo.endCursor,
      search: this.shotTypes.search
    };

    this.#graphQLService.getData<ShotType>(GetQuery.GET_SHOT_TYPES, filter)
      .pipe(map(res => res['data']['shotTypes']), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: WrapperResultGraphQL<ShotType>) => {
          this.shotTypes.data = !this.colors.pageInfo.endCursor ? UtilityFunctions.DeepClone(data.nodes) : this.shotTypes.data.concat(UtilityFunctions.DeepClone(data.nodes));
          this.colors.pageInfo = { ...data.pageInfo };
        }
      });
  }

  // Media Type
  private getMediaTypes(page: number) {
    this.mediaTypes.page = page;
    this.#graphQLService.getData<MediaFormat>(GetQuery.GET_MEDIA_FORMATS)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['mediaFormats']))
      .subscribe((data: WrapperResultGraphQL<MediaFormat>) => {
        this.mediaTypes.data = !this.mediaTypes.page ? UtilityFunctions.DeepClone(data.nodes) : this.mediaTypes.data.concat(UtilityFunctions.DeepClone(data.nodes));
      });
  }

  getMediaTypeLabel(id: number): string {
    const found = this.mediaTypes.data.find(m => m.id === id);
    return found ? found.code : '';
  }

  onMediaTypesFilterChange(ev: any) {
    // clearTimeout(this.shotTypes.inputTimer);
    // this.shotTypes.inputTimer = setTimeout(() => {
    //   this.filteredMediaTypes = this.mediaTypes.filter(f => f.mediaTypeCode.toLowerCase().indexOf(ev.toLowerCase()) >= 0);
    // }, 750);
  }

  onChangeMediaType(type: TemplateShotType) {
    type.mediaFormatId = undefined;
    type.aspectRatioId = undefined;
    type.codifiedUsedId = undefined;
    if (this.templates.tmp?.hasSizeGroup && this.changeAllColumn) {
      this.templates.tmp.shotTypes.filter(t => t.sizeIndex === type.sizeIndex && t.sizeGroupId !== type.sizeGroupId).forEach(t => t.shotTypeCode = type.shotTypeCode);
    }
  }

  onSelectMediaType(type: TemplateShotType, mediaType: MediaFormat) {
    const newType = new TemplateShotType({
      shotTypeCode: type.shotTypeCode,
      shotTypeId: type.shotTypeId,
      shotTypeName: type.shotTypeName,
      mediaFormatId: mediaType.id,
      aspectRatioId: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].aspectRatioId,
      codifiedUsedId: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].codifiedUsedId,
      codifiedUsedCode: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].codifiedUsedCode,
      codifiedUsedName: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].codifiedUsedName,
      showFormat: false,
      sizeGroupId: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].sizeGroupId,
      sizeGroupName: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].sizeGroupName,
      sizeIndex: this.templates.tmp.shotTypes[this.mediaTypes.tmpIndex].sizeIndex
    });

    this.templates.tmp.shotTypes.splice(this.mediaTypes.tmpIndex, 1, newType);

    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.forEach(tp => {
        if (tp.sizeIndex === newType.sizeIndex && tp.sizeGroupId !== newType.sizeGroupId) {
          tp.shotTypeId = newType.shotTypeId;
          tp.shotTypeCode = newType.shotTypeCode;
          tp.shotTypeName = newType.shotTypeName;
        }
      });
    }
  }

  onBlurMediaType(index: number) {
    if (!this.optionsHover) this.templates.tmp.shotTypes[index].showFormat = false;
  }

  onFocusMediaType(index: number) {
    this.mediaTypes.tmpIndex = index;
    setTimeout(() => {
      this.templates.tmp!.shotTypes[index].showFormat = true;
      if (!this.mediaTypes.data.length) this.getMediaTypes(0);
    });
  }

  getMediaTypeCode(type: TemplateShotType): string {
    return this.mediaTypes.data.find(el => el.id === type.mediaFormatId)?.code || '';
  }

  // Aspect Ratio
  private getAspectRatios(page: number) {
    this.aspectRatios.page = page;

    this.#graphQLService.getData(GetQuery.GET_ASPECT_RATIOS)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['aspectRatios']))
      .subscribe((data: WrapperResultGraphQL<AspectRatio>) => {
        this.aspectRatios.data = !this.aspectRatios.page ? UtilityFunctions.DeepClone(data.nodes) : this.aspectRatios.data.concat(UtilityFunctions.DeepClone(data.nodes));
      });
  }

  getAspectRatioLabel(id: number): string {
    const found = this.aspectRatios.data.find(m => m.id === id);
    return found ? found.code : '';
  }

  onAspectRatiosFilterChange(ev: any) {
    // clearTimeout(this.shotTypes.inputTimer);
    // this.shotTypes.inputTimer = setTimeout(() => {
    //   this.filteredAspectRatios = this.aspectRatios.filter(f => f.aspectRatioCode.toLowerCase().indexOf(ev.toLowerCase()) >= 0);
    // }, 750);
  }

  onChangeAspectRatio(type: TemplateShotType) {
    type.aspectRatioId = undefined;
    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.filter(t => t.sizeIndex === type.sizeIndex && t.sizeGroupId !== type.sizeGroupId).forEach(t => t.shotTypeCode = type.shotTypeCode);
    }
  }

  onSelectAspectRatio(type: TemplateShotType, aspectRatio: AspectRatio) {
    const newType = new TemplateShotType({
      shotTypeCode: type.shotTypeCode,
      shotTypeId: type.shotTypeId,
      shotTypeName: type.shotTypeName,
      aspectRatioId: aspectRatio.id,
      mediaFormatId: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].mediaFormatId,
      codifiedUsedId: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].codifiedUsedId,
      codifiedUsedCode: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].codifiedUsedCode,
      codifiedUsedName: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].codifiedUsedName,
      showAspectRatio: false,
      sizeGroupId: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].sizeGroupId,
      sizeGroupName: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].sizeGroupName,
      sizeIndex: this.templates.tmp.shotTypes[this.aspectRatios.tmpIndex].sizeIndex
    });

    this.templates.tmp.shotTypes.splice(this.aspectRatios.tmpIndex, 1, newType);

    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.forEach(tp => {
        if (tp.sizeIndex === newType.sizeIndex && tp.sizeGroupId !== newType.sizeGroupId) {
          tp.shotTypeId = newType.shotTypeId;
          tp.shotTypeCode = newType.shotTypeCode;
          tp.shotTypeName = newType.shotTypeName;
        }
      });
    }
  }

  onBlurAspectRatio(index: number) {
    if (!this.optionsHover) this.templates.tmp.shotTypes[index].showAspectRatio = false;
  }

  onFocusAspectRatio(index: number) {
    this.aspectRatios.tmpIndex = index;
    setTimeout(() => {
      this.templates.tmp!.shotTypes[index].showAspectRatio = true;
      if (!this.aspectRatios.data.length) this.getAspectRatios(0);
    });
  }

  getAspectRatio(type: TemplateShotType): string {
    return this.aspectRatios.data.find(el => el.id === type.aspectRatioId)?.code || '';
  }

  // Codified Used
  private getCodifiedUsed(page: number) {
    this.codifiedUseds.page = page;

    this.#graphQLService.getData<CodifiedUsed>(GetQuery.GET_CODIFIED_USEDS, { search: this.codifiedUseds.search, first: this.codifiedUseds.size, after: this.codifiedUseds.pageInfo.endCursor })
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['codifiedUseds']))
      .subscribe((data: WrapperResultGraphQL<CodifiedUsed>) => {
        this.codifiedUseds.data = !this.codifiedUseds.pageInfo.endCursor ? UtilityFunctions.DeepClone(data.nodes) : this.codifiedUseds.data.concat(UtilityFunctions.DeepClone(data.nodes));
        this.codifiedUseds.pageInfo = UtilityFunctions.DeepClone(data.pageInfo)
      });

  }

  onCodifiedUsedsFilterChange(ev: any) {
    clearTimeout(this.codifiedUseds.inputTimer);
    this.codifiedUseds.inputTimer = setTimeout(() => {
      this.getCodifiedUsed(0);
    }, 750);
  }

  onChangeCodifiedUsedName(type: TemplateShotType, search: string) {
    type.codifiedUsedId = undefined;
    type.codifiedUsedCode = undefined
    type.codifiedUsedName = undefined

    clearTimeout(this.codifiedUseds.inputTimer);
    this.codifiedUseds.inputTimer = setTimeout(() => {
      this.companyService.getCodifiedUseds(
        this.codifiedUseds.page,
        this.codifiedUseds.size,
        search,
        this.codifiedUseds.loaderId
      ).pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(data => {
          this.codifiedUseds.data = !this.codifiedUseds.page ? data : this.codifiedUseds.data.concat(data);
        });
    }, 500);
  }

  onChangeCodifiedUsedCode(type: TemplateShotType) {
    type.codifiedUsedId = undefined;
    type.codifiedUsedCode = undefined
    type.codifiedUsedName = undefined
    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.filter(t => t.sizeIndex === type.sizeIndex && t.sizeGroupId !== type.sizeGroupId).forEach(t => t.codifiedUsedCode = type.codifiedUsedCode);
    }
  }

  onBlurCodifiedUsed(index: number) {
    if (!this.optionsHover) this.templates.tmp.shotTypes[index].showCodifiedUsedsOptions = false;
  }

  onSelectCodifiedUsed(type: TemplateShotType, codifiedUsed: CodifiedUsed) {
    if (this.isCodifiedUsedSelected(codifiedUsed.id)) return;
    const newType = new TemplateShotType({
      shotTypeCode: type.shotTypeCode,
      shotTypeId: type.shotTypeId,
      shotTypeName: type.shotTypeName,
      aspectRatioId: this.templates.tmp.shotTypes[this.codifiedUseds.tmpIndex].aspectRatioId,
      mediaFormatId: this.templates.tmp.shotTypes[this.codifiedUseds.tmpIndex].mediaFormatId,
      codifiedUsedId: codifiedUsed.id,
      codifiedUsedCode: codifiedUsed.code,
      codifiedUsedName: codifiedUsed.description,
      showOptions: false,
      sizeGroupId: this.templates.tmp.shotTypes[this.codifiedUseds.tmpIndex].sizeGroupId,
      sizeGroupName: this.templates.tmp.shotTypes[this.codifiedUseds.tmpIndex].sizeGroupName,
      sizeIndex: this.templates.tmp.shotTypes[this.codifiedUseds.tmpIndex].sizeIndex
    });
    this.templates.tmp.shotTypes.splice(this.codifiedUseds.tmpIndex, 1, newType);
    if (this.differentShotsForSizeGroups && this.changeAllColumn) {
      this.templates.tmp.shotTypes.forEach(tp => {
        if (tp.sizeIndex === newType.sizeIndex && tp.sizeGroupId !== newType.sizeGroupId) {
          tp.shotTypeId = newType.shotTypeId;
          tp.shotTypeCode = newType.shotTypeCode;
          tp.shotTypeName = newType.shotTypeName;
        }
      });
    }
  }

  isCodifiedUsedSelected(id: number, type?: TemplateShotType): boolean {
    const tmp = this.templates.tmp!;

    if (!this.differentShotsForSizeGroups) {
      return tmp.shotTypes.some(el => el.codifiedUsedId === id);
    }

    if (!!type)
      return tmp.shotTypes.some(el => el.codifiedUsedId === id && el.sizeGroupId === type?.sizeGroupId);

    return false;
  }

  onFocusCodifiedUsed(index: number) {
    this.codifiedUseds.tmpIndex = index;
    setTimeout(() => {
      this.templates.tmp!.shotTypes[index].showCodifiedUsedsOptions = true;
      if (!this.codifiedUseds.data.length) this.getCodifiedUsed(0);
    });
  }

  getCodifiedUsedDesc(type: TemplateShotType): string {
    return this.codifiedUseds.data.find(el => el.id === type.codifiedUsedId)?.description || '';
  }

  getCodifiedUsedCode(type: TemplateShotType): string {
    return this.codifiedUseds.data.find(el => el.id === type.codifiedUsedId)?.code || '';
  }

  getCodifiedUsedlabel(type: TemplateShotType): string {
    const codifiedUsed = this.codifiedUseds.data.find(el => el.id === type.codifiedUsedId);
    return codifiedUsed ? `${codifiedUsed.code} - ${codifiedUsed.description}` : '';
  }
}
